import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Edit2, Trash2, Check, X, Save, CreditCard, Landmark, DollarSign, ChevronDown, ChevronUp, BarChart3, AlertCircle, Download } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import DeleteConfirmModal from '@/components/merchant-fee-manager/DeleteConfirmModal';
import PaymentTypeSection from '@/components/merchant-fee-manager/PaymentTypeSection';
import { AddProcessorModal, AddPaymentTypeModal } from '@/components/merchant-fee-manager/AddModals';


export default function MerchantFeeManager() {
  const navigate = useNavigate();
  const [processors, setProcessors] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState({ Card: [], ACH: [] });
  const [allServices, setAllServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedProcessor, setExpandedProcessor] = useState(null);
  const [expandedProcessorType, setExpandedProcessorType] = useState({});
  
  // Get user role
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = user.role || '';
  const canEdit = ['cfo', 'admin'].includes(userRole.toLowerCase());
  
  // Add/Edit states
  const [showAddProcessor, setShowAddProcessor] = useState(false);
  const [showAddPaymentType, setShowAddPaymentType] = useState(false);
  const [editingProcessor, setEditingProcessor] = useState(null);
  const [editingPaymentType, setEditingPaymentType] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Matrix editing
  const [editingMatrix, setEditingMatrix] = useState({});
  const [matrixChanges, setMatrixChanges] = useState({});
  const [rawMatrixValues, setRawMatrixValues] = useState({}); // Store raw string values during editing
  const [feeTypes, setFeeTypes] = useState({}); // Track if fee is % or $ for ACH processors

  // Processor types
  const PROCESSOR_TYPES = ['Card Processor', 'ACH Processor'];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [processorsRes, paymentTypesRes, servicesRes] = await Promise.all([
        api.get('/processors'),
        api.get('/payment-types'),
        api.get('/admin/services')
      ]);
      
      setProcessors(processorsRes.data.processors || []);
      setPaymentTypes(paymentTypesRes.data.payment_types || { Card: [], ACH: [] });
      setAllServices(servicesRes.data.services || []);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(error.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddPaymentType = async () => {
    const newType = editingPaymentType || {};
    if (!newType.name?.trim() || !newType.code?.trim()) {
      toast.error('Please enter both name and code');
      return;
    }

    try {
      await api.post(
        '/payment-types',
        { name: newType.name, code: newType.code.toUpperCase(), category: newType.category || 'Card' }
      );

      toast.success('Payment type added successfully');
      setShowAddPaymentType(false);
      setEditingPaymentType(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add payment type');
    }
  };

  const handleUpdatePaymentType = async (paymentTypeId) => {
    try {
      await api.put(
        `/payment-types/${paymentTypeId}`,
        { name: editingPaymentType.name, code: editingPaymentType.code }
      );
      toast.success('Updated successfully');
      setEditingPaymentType(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handleDeletePaymentType = async (paymentTypeId, paymentTypeName) => {
    setDeleteConfirm({ type: 'paymentType', id: paymentTypeId, name: paymentTypeName });
  };

  const handleAddProcessor = async () => {
    const proc = editingProcessor || {};
    if (!proc.name?.trim()) {
      toast.error('Please enter processor name');
      return;
    }
    if (!proc.processor_type?.trim()) {
      toast.error('Please select processor type');
      return;
    }

    try {
      await api.post('/processors', { 
        name: proc.name,
        processor_type: proc.processor_type,
        fees: proc.fees || {}
      });
      toast.success('Processor added');
      setShowAddProcessor(false);
      setEditingProcessor(null);
      loadData();
    } catch (error) {
      toast.error('Failed to add processor');
    }
  };

  const handleUpdateProcessor = async (processorId) => {
    try {
      await api.put(`/processors/${processorId}`, {
        name: editingProcessor.name,
        processor_type: editingProcessor.processor_type
      });
      toast.success('Processor updated');
      setEditingProcessor(null);
      loadData();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDeleteProcessor = async (processorId, processorName) => {
    setDeleteConfirm({ type: 'processor', id: processorId, name: processorName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;

    try {
      
      if (deleteConfirm.type === 'processor') {
        await api.delete(`/processors/${deleteConfirm.id}`);
        toast.success('Processor deleted successfully');
      } else {
        await api.delete(`/payment-types/${deleteConfirm.id}`);
        toast.success('Payment type deleted successfully');
      }
      
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
      setDeleteConfirm(null);
    }
  };

  const handleAssignService = async (serviceId, processorId) => {
    try {
      await api.put(`/services/${serviceId}/processor`, { processor_id: processorId });
      toast.success('Service assigned');
      loadData();
    } catch (error) {
      toast.error('Failed to assign');
    }
  };

  const handleUnassignService = async (serviceId) => {
    try {
      await api.put(`/services/${serviceId}/processor`, { processor_id: null });
      toast.success('Service unassigned');
      loadData();
    } catch (error) {
      toast.error('Failed to unassign');
    }
  };

  const getProcessorServices = (processorId) => {
    // Return ALL services for the matrix, not just assigned ones
    return allServices;
  };

  const getUnassignedServices = () => {
    // No longer needed - all services are shown by default
    return [];
  };

  const getProcessorsByType = () => {
    const grouped = {};
    processors.forEach(proc => {
      const type = proc.processor_type || 'Uncategorized';
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(proc);
    });
    return grouped;
  };

  const handleMatrixCellChange = (processorId, serviceId, paymentCode, value) => {
    // Store raw string value for display
    const key = `${processorId}-${serviceId}-${paymentCode}`;
    setRawMatrixValues(prev => ({ ...prev, [key]: value }));
    
    // Get fee type (default to percentage for backward compatibility)
    const feeTypeKey = `${processorId}-${serviceId}-${paymentCode}`;
    const isFlat = feeTypes[feeTypeKey] === 'flat';
    
    // Store parsed value
    const parsedValue = parseFloat(value) || 0;
    const finalValue = isFlat ? parsedValue : parsedValue / 100; // Don't divide by 100 if it's a flat fee
    
    setMatrixChanges(prev => ({
      ...prev,
      [processorId]: {
        ...prev[processorId],
        [serviceId]: {
          ...(prev[processorId]?.[serviceId] || {}),
          [paymentCode]: finalValue
        }
      }
    }));
  };

  const toggleFeeType = (processorId, serviceId, paymentCode) => {
    const key = `${processorId}-${serviceId}-${paymentCode}`;
    const currentType = feeTypes[key] || 'percentage';
    const newType = currentType === 'flat' ? 'percentage' : 'flat';
    
    setFeeTypes(prev => ({
      ...prev,
      [key]: newType
    }));
    
    // Clear the raw value so it recalculates with new type
    setRawMatrixValues(prev => {
      const newValues = { ...prev };
      delete newValues[key];
      return newValues;
    });
    
    // Show feedback
    toast.info(`Switched to ${newType === 'flat' ? 'flat fee ($)' : 'percentage (%)'}`);
  };

  const saveMatrixChanges = async (processorId) => {
    try {
      const serviceFees = matrixChanges[processorId] || {};
      
      // Extract fee types for this processor
      const processorFeeTypes = {};
      Object.keys(feeTypes).forEach(key => {
        if (key.startsWith(`${processorId}-`)) {
          // Key format: processorId-serviceId-paymentCode
          const parts = key.split('-');
          if (parts.length >= 3) {
            const serviceId = parts.slice(1, -1).join('-'); // Handle UUIDs with dashes
            const paymentCode = parts[parts.length - 1];
            
            if (!processorFeeTypes[serviceId]) {
              processorFeeTypes[serviceId] = {};
            }
            processorFeeTypes[serviceId][paymentCode] = feeTypes[key];
          }
        }
      });
      
      await api.put(`/processors/${processorId}/service-fees`, {
        service_fees: serviceFees,
        fee_types: processorFeeTypes  // Send fee types to backend
      });
      
      // Set flag to reload reference data in Calculator
      localStorage.setItem('reload_reference_data', 'true');
      
      toast.success('Fees saved successfully');
      setMatrixChanges(prev => ({ ...prev, [processorId]: {} }));
      setEditingMatrix(prev => ({ ...prev, [processorId]: false }));
      setRawMatrixValues({}); // Clear raw values after save
      loadData();
    } catch (error) {
      toast.error('Failed to save fees');
    }
  };

  const getMatrixValue = (processor, serviceId, paymentCode, asRaw = false) => {
    const feeTypeKey = `${processor.id}-${serviceId}-${paymentCode}`;
    const isFlat = feeTypes[feeTypeKey] === 'flat';
    
    // If requesting raw value for input display
    if (asRaw) {
      const key = `${processor.id}-${serviceId}-${paymentCode}`;
      if (rawMatrixValues[key] !== undefined) {
        return rawMatrixValues[key];
      }
    }
    
    let value = 0;
    
    if (matrixChanges[processor.id]?.[serviceId]?.[paymentCode] !== undefined) {
      value = matrixChanges[processor.id][serviceId][paymentCode];
    } else if (processor.service_fees?.[serviceId]?.[paymentCode] !== undefined) {
      value = processor.service_fees[serviceId][paymentCode];
    } else if (processor.fees?.[paymentCode] !== undefined) {
      value = processor.fees[paymentCode];
    }
    
    // Convert to appropriate display format
    if (asRaw) {
      return isFlat ? value.toFixed(2) : (value * 100).toFixed(2);
    }
    
    return isFlat ? value : value * 100;
  };

  const getFeeType = (processorId, serviceId, paymentCode) => {
    const key = `${processorId}-${serviceId}-${paymentCode}`;
    return feeTypes[key] || 'percentage';
  };

  const toggleProcessorType = (type) => {
    setExpandedProcessorType(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleExportCSV = () => {
    try {
      // Build service ID → {name, code} lookup
      const svcMap = {};
      allServices.forEach(s => { svcMap[s.id] = s; });

      const rows = [];
      rows.push([
        'Processor', 'Processor Type', 'Is Default',
        'Service Name', 'Service Code',
        'ACH Fee (%)', 'CC Fee (%)', 'AMEX Fee (%)', 'DEBIT Fee (%)',
        'Fee Source',
      ]);

      const fmtPct = (val) => (val != null && val !== '') ? (parseFloat(val) * 100).toFixed(4) : '—';

      const getFee = (processor, serviceId, paymentCode) => {
        // Priority: service-level override → processor global fees → not set
        const svcFees = processor.service_fees?.[serviceId];
        if (svcFees && paymentCode in svcFees) return svcFees[paymentCode];
        if (processor.fees && paymentCode in processor.fees) return processor.fees[paymentCode];
        return null;
      };

      processors.forEach(proc => {
        // Collect all service IDs this processor has fee data for
        const svcIds = new Set([
          ...Object.keys(proc.service_fees || {}),
          ...allServices.map(s => s.id),  // include all services (global fee applies)
        ]);

        svcIds.forEach(svcId => {
          const svc = svcMap[svcId];
          if (!svc) return; // skip unknown IDs

          const achRaw = getFee(proc, svcId, 'ACH');
          const ccRaw  = getFee(proc, svcId, 'CC');
          const amexRaw = getFee(proc, svcId, 'AMEX');
          const debitRaw = getFee(proc, svcId, 'DEBIT');

          // Determine fee source label
          const svcFees = proc.service_fees?.[svcId];
          const hasServiceOverride = svcFees && Object.keys(svcFees).length > 0;
          const source = hasServiceOverride ? 'Service Override' : (Object.keys(proc.fees || {}).length ? 'Global Default' : '—');

          rows.push([
            proc.name,
            proc.processor_type || '',
            proc.is_default ? 'Yes' : 'No',
            svc.name,
            svc.code || '',
            fmtPct(achRaw),
            fmtPct(ccRaw),
            fmtPct(amexRaw),
            fmtPct(debitRaw),
            source,
          ]);
        });
      });

      // Sort: processor name → service name
      const header = rows[0];
      const data = rows.slice(1).sort((a, b) => a[0].localeCompare(b[0]) || a[3].localeCompare(b[3]));

      const escape = v => {
        const s = String(v ?? '');
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csv = [header, ...data].map(r => r.map(escape).join(',')).join('\r\n');

      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `GW_Merchant_Fees_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${data.length} rows (${processors.length} processors × ${allServices.length} services)`);
    } catch (err) {
      console.error('CSV export error:', err);
      toast.error('Failed to export CSV');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button onClick={() => navigate(-1)} className="text-slate-600 hover:text-slate-900">
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">Merchant Fee Manager</h1>
                <p className="text-sm text-slate-600">Configure processor types, processors, payment types, and service fees (all services displayed automatically)</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleExportCSV}
                disabled={loading}
                data-testid="export-merchant-fees-csv-btn"
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded-lg font-medium text-sm disabled:opacity-50 transition-colors"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
              {canEdit ? (
                <>
                  <button onClick={() => { setShowAddPaymentType(true); setEditingPaymentType({ category: 'Card' }); }} className="btn-secondary">
                    <Plus className="w-4 h-4 mr-2" />Add Payment Type
                  </button>
                  <button onClick={() => { setShowAddProcessor(true); setEditingProcessor({ name: '', processor_type: 'Card Processor', fees: {} }); }} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />Add Processor
                  </button>
                </>
              ) : (
                <div className="px-4 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-800 font-medium">View Only - CFO/Admin required to edit</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Payment Types Section - EDITABLE */}
        <PaymentTypeSection 
          paymentTypes={paymentTypes}
          editingPaymentType={editingPaymentType}
          setEditingPaymentType={setEditingPaymentType}
          handleUpdatePaymentType={handleUpdatePaymentType}
          handleDeletePaymentType={handleDeletePaymentType}
        />

        {/* Processors Section - Grouped by Processor Type */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Processor Types (Groups) */}
            {Object.entries(getProcessorsByType()).map(([processorType, processorsInType]) => (
              <div key={processorType} className="card p-6">
                {/* Processor Type Header */}
                <div 
                  className="flex items-center justify-between mb-4 cursor-pointer hover:bg-slate-50 p-3 rounded -mx-3"
                  onClick={() => toggleProcessorType(processorType)}
                >
                  <div className="flex items-center space-x-3">
                    <DollarSign className="w-6 h-6 text-purple-600" />
                    <div>
                      <h3 className="text-xl font-bold text-purple-900">{processorType}</h3>
                      <p className="text-sm text-slate-600">{processorsInType.length} processor{processorsInType.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  <button className="p-2 hover:bg-slate-100 rounded">
                    {expandedProcessorType[processorType] ? (
                      <ChevronUp className="w-5 h-5 text-slate-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    )}
                  </button>
                </div>

                {/* Processors under this type */}
                {expandedProcessorType[processorType] !== false && (
                  <div className="space-y-4 ml-6 border-l-2 border-purple-200 pl-6">
                    {processorsInType.map(processor => {
                      const processorServices = getProcessorServices(processor.id);
                      const isExpanded = expandedProcessor === processor.id;
                      const isEditingName = editingProcessor?.id === processor.id;
                      
                      return (
                        <div key={processor.id} className="card p-4 bg-white">
                          {/* Processor Header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-3 flex-1">
                              {isEditingName ? (
                                <div className="flex items-center space-x-2 flex-1">
                                  <input
                                    type="text"
                                    value={editingProcessor.name}
                                    onChange={(e) => setEditingProcessor({ ...editingProcessor, name: e.target.value })}
                                    className="input-field"
                                    placeholder="Processor Name"
                                  />
                                  <input
                                    type="text"
                                    value={editingProcessor.processor_type}
                                    onChange={(e) => setEditingProcessor({ ...editingProcessor, processor_type: e.target.value })}
                                    className="input-field"
                                    placeholder="Processor Type"
                                  />
                                  <button onClick={() => handleUpdateProcessor(processor.id)} className="text-green-600 hover:text-green-700">
                                    <Check className="w-5 h-5" />
                                  </button>
                                  <button onClick={() => setEditingProcessor(null)} className="text-slate-600 hover:text-slate-700">
                                    <X className="w-5 h-5" />
                                  </button>
                                </div>
                              ) : (
                                <div>
                                  <h4 className="text-lg font-bold">{processor.name}</h4>
                                  <div className="flex space-x-2 mt-1">
                                    {processor.is_default && <span className="text-xs text-indigo-600 font-medium">Default</span>}
                                    <span className="text-xs text-slate-500">{processorServices.length} services</span>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              {!isEditingName && (
                                <>
                                  <button onClick={() => setEditingProcessor({ ...processor })} className="text-blue-600 hover:text-blue-700 p-2">
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  {!processor.is_default && (
                                    <button onClick={() => handleDeleteProcessor(processor.id, processor.name)} className="text-red-600 hover:text-red-700 p-2">
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </>
                              )}
                              <button 
                                onClick={() => setExpandedProcessor(isExpanded ? null : processor.id)}
                                className="btn-secondary py-1 text-sm"
                              >
                                {isExpanded ? 'Hide Matrix' : 'Show Matrix'}
                              </button>
                            </div>
                          </div>

                          {/* Service Fee Matrix */}
                          {isExpanded && (
                            <div className="border-t pt-4 mt-4">
                              <div className="flex justify-between items-center mb-4">
                                <h4 className="text-base font-bold text-slate-900 flex items-center">
                                  <BarChart3 className="w-5 h-5 mr-2 text-indigo-600" />
                                  Service Fee Matrix - {processor.name}
                                </h4>
                                <div className="flex space-x-2">
                                  {editingMatrix[processor.id] ? (
                                    <>
                                      <button onClick={() => saveMatrixChanges(processor.id)} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 font-semibold flex items-center">
                                        <Save className="w-4 h-4 mr-1" />
                                        Save Changes
                                      </button>
                                      <button onClick={() => { 
                                        setEditingMatrix(prev => ({ ...prev, [processor.id]: false })); 
                                        setMatrixChanges(prev => ({ ...prev, [processor.id]: {} }));
                                        setRawMatrixValues({}); // Clear raw values on cancel
                                      }} className="px-4 py-2 text-sm bg-slate-300 text-slate-700 rounded-lg hover:bg-slate-400 font-semibold">
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <button onClick={() => {
                                      setEditingMatrix(prev => ({ ...prev, [processor.id]: true }));
                                      setRawMatrixValues({}); // Clear raw values when entering edit mode
                                    }} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-semibold flex items-center">
                                      <Edit2 className="w-4 h-4 mr-1" />
                                      Edit Fees
                                    </button>
                                  )}
                                </div>
                              </div>

                              {processorServices.length === 0 ? (
                                <div className="text-center py-8 bg-slate-50 rounded">
                                  <p className="text-slate-600 mb-4">No services assigned to this processor yet</p>
                                  <div className="space-y-2">
                                    <p className="text-sm text-slate-500">Assign services:</p>
                                    {getUnassignedServices().slice(0, 5).map(service => (
                                      <button
                                        key={service.id}
                                        onClick={() => handleAssignService(service.id, processor.id)}
                                        className="block mx-auto px-4 py-2 bg-white border rounded hover:bg-indigo-50 text-sm"
                                      >
                                        {service.name}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="bg-white rounded-xl shadow-md overflow-hidden border-2 border-slate-200">
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
                                        <tr>
                                          <th className="text-left py-3 px-4 font-bold sticky left-0 bg-indigo-600 z-10">
                                            Payment Type
                                          </th>
                                          {processorServices.map(service => (
                                            <th key={service.id} className="text-center py-3 px-4 font-bold min-w-[120px]">
                                              <div className="flex flex-col items-center">
                                                <span className="font-bold">{service.name}</span>
                                                <span className="text-xs font-normal opacity-80">({service.code})</span>
                                              </div>
                                            </th>
                                          ))}
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-slate-200">
                                        {/* Card Payment Types - ONLY for Card Processors */}
                                        {processor.processor_type === 'Card Processor' && paymentTypes.Card?.length > 0 && (
                                          <>
                                            <tr className="bg-indigo-100">
                                              <td colSpan={processorServices.length + 1} className="py-2 px-4 font-bold text-indigo-900">
                                                <div className="flex items-center">
                                                  <CreditCard className="w-4 h-4 mr-2" />
                                                  Card Payments
                                                </div>
                                              </td>
                                            </tr>
                                            {paymentTypes.Card.map(pt => (
                                              <tr key={pt.code} className="hover:bg-indigo-50 transition-colors">
                                                <td className="py-3 px-4 font-semibold text-slate-900 sticky left-0 bg-white">
                                                  {pt.name}
                                                  <span className="text-xs text-slate-500 ml-2">({pt.code})</span>
                                                </td>
                                                {processorServices.map(service => (
                                                  <td key={service.id} className="text-center py-3 px-4">
                                                    {editingMatrix[processor.id] ? (
                                                      <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        value={rawMatrixValues[`${processor.id}-${service.id}-${pt.code}`] !== undefined 
                                                          ? rawMatrixValues[`${processor.id}-${service.id}-${pt.code}`]
                                                          : getMatrixValue(processor, service.id, pt.code).toFixed(2)}
                                                        onChange={(e) => handleMatrixCellChange(processor.id, service.id, pt.code, e.target.value)}
                                                        onBlur={(e) => {
                                                          const numValue = parseFloat(e.target.value);
                                                          if (!isNaN(numValue)) {
                                                            const key = `${processor.id}-${service.id}-${pt.code}`;
                                                            setRawMatrixValues(prev => ({ ...prev, [key]: numValue.toFixed(2) }));
                                                          }
                                                        }}
                                                        className="w-24 px-2 py-2 border-2 border-indigo-300 rounded text-center font-bold focus:ring-2 focus:ring-indigo-500"
                                                        placeholder="0.00"
                                                      />
                                                    ) : (
                                                      <span className="font-bold text-indigo-700 text-base">
                                                        {getMatrixValue(processor, service.id, pt.code).toFixed(2)}%
                                                      </span>
                                                    )}
                                                  </td>
                                                ))}
                                              </tr>
                                            ))}
                                          </>
                                        )}
                                        
                                        {/* ACH Payment Types - ONLY for ACH Processors */}
                                        {processor.processor_type === 'ACH Processor' && paymentTypes.ACH?.length > 0 && (
                                          <>
                                            <tr className="bg-green-100">
                                              <td colSpan={processorServices.length + 1} className="py-2 px-4 font-bold text-green-900">
                                                <div className="flex items-center">
                                                  <Landmark className="w-4 h-4 mr-2" />
                                                  ACH Payments
                                                </div>
                                              </td>
                                            </tr>
                                            {paymentTypes.ACH.map(pt => (
                                              <tr key={pt.code} className="hover:bg-green-50 transition-colors">
                                                <td className="py-3 px-4 font-semibold text-slate-900 sticky left-0 bg-white">
                                                  {pt.name}
                                                  <span className="text-xs text-slate-500 ml-2">({pt.code})</span>
                                                </td>
                                                {processorServices.map(service => (
                                                  <td key={service.id} className="text-center py-3 px-4">
                                                    {editingMatrix[processor.id] ? (
                                                      <input
                                                        type="number"
                                                        step="0.01"
                                                        min="0"
                                                        max="100"
                                                        value={getMatrixValue(processor, service.id, pt.code)}
                                                                    onFocus={(e) => setTimeout(() => e.target.select(), 0)}
            onClick={(e) => e.target.select()}
                                                        onChange={(e) => handleMatrixCellChange(processor.id, service.id, pt.code, e.target.value)}
                                                        className="w-24 px-2 py-2 border-2 border-green-300 rounded text-center font-bold focus:ring-2 focus:ring-green-500"
                                                        placeholder="0.00"
                                                      />
                                                    ) : (
                                                      <span className="font-bold text-green-700 text-base">
                                                        {getMatrixValue(processor, service.id, pt.code).toFixed(2)}%
                                                      </span>
                                                    )}
                                                  </td>
                                                ))}
                                              </tr>
                                            ))}
                                          </>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                              
                              <div className="mt-3 text-xs text-slate-500 text-center bg-slate-100 py-2 rounded">
                                <strong>Note:</strong> Enter percentages (e.g., 2.50 for 2.50%). Click Edit Fees to modify rates.
                              </div>

                              <div className="border-t pt-4 mt-3">
                              <div className="flex justify-between items-center mb-4">
                                <h5 className="text-sm font-semibold">Service Fee Matrix (All Services)</h5>
                                <div className="flex space-x-2">
                                  {editingMatrix[processor.id] ? (
                                    <>
                                      <button onClick={() => saveMatrixChanges(processor.id)} className="btn-primary text-sm py-1">
                                        <Save className="w-3 h-3 mr-1" />Save Changes
                                      </button>
                                      <button onClick={() => { 
                                        setEditingMatrix(prev => ({ ...prev, [processor.id]: false })); 
                                        setMatrixChanges(prev => ({ ...prev, [processor.id]: {} }));
                                        setRawMatrixValues({}); // Clear raw values on cancel
                                      }} className="btn-secondary text-sm py-1">
                                        Cancel
                                      </button>
                                    </>
                                  ) : (
                                    <button onClick={() => {
                                      setEditingMatrix(prev => ({ ...prev, [processor.id]: true }));
                                      setRawMatrixValues({}); // Clear raw values when entering edit mode
                                    }} className="btn-secondary text-sm py-1">
                                      <Edit2 className="w-3 h-3 mr-1" />Edit Fees
                                    </button>
                                  )}
                                </div>
                              </div>

                              {processorServices.length === 0 ? (
                                <div className="text-center py-8 bg-slate-50 rounded">
                                  <p className="text-slate-600">Loading services...</p>
                                </div>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm border-collapse">
                                    <thead>
                                      <tr className="border-b-2 border-slate-300 bg-slate-100">
                                        <th className="text-left py-3 px-4 font-bold text-slate-800 sticky left-0 bg-slate-100 z-10">
                                          Payment Type
                                        </th>
                                        {processorServices.map(service => (
                                          <th key={service.id} className="text-center py-3 px-4 font-semibold text-slate-800 min-w-[100px]">
                                            <div className="flex flex-col items-center">
                                              <span className="font-bold">{service.name}</span>
                                            </div>
                                          </th>
                                        ))}
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {/* Card Payment Types - ONLY for Card Processors */}
                                      {processor.processor_type === 'Card Processor' && paymentTypes.Card?.length > 0 && (
                                        <>
                                          <tr className="bg-indigo-100">
                                            <td colSpan={processorServices.length + 1} className="py-2 px-4 font-bold text-indigo-900">
                                              <div className="flex items-center">
                                                <CreditCard className="w-4 h-4 mr-2" />
                                                Card Payments
                                              </div>
                                            </td>
                                          </tr>
                                          {paymentTypes.Card.map(pt => (
                                            <tr key={pt.code} className="border-b border-slate-200 hover:bg-slate-50">
                                              <td className="py-3 px-4 font-semibold text-slate-700 sticky left-0 bg-white">
                                                {pt.name}
                                              </td>
                                              {processorServices.map(service => (
                                                <td key={service.id} className="text-center py-3 px-4">
                                                  {editingMatrix[processor.id] ? (
                                                    <input
                                                      type="text"
                                                      value={getMatrixValue(processor, service.id, pt.code, true)}
                                                      onChange={(e) => {
                                                        const value = e.target.value;
                                                        // Allow numbers and single decimal point with up to 2 decimal places
                                                        if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                                          handleMatrixCellChange(processor.id, service.id, pt.code, value);
                                                        }
                                                      }}
                                                      className="w-24 px-2 py-2 border border-slate-300 rounded text-center focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                      placeholder="0.00"
                                                    />
                                                  ) : (
                                                    <span className="font-mono text-slate-900 font-semibold">
                                                      {getMatrixValue(processor, service.id, pt.code).toFixed(2)}%
                                                    </span>
                                                  )}
                                                </td>
                                              ))}
                                            </tr>
                                          ))}
                                        </>
                                      )}
                                      
                                      {/* ACH Payment Types - ONLY for ACH Processors */}
                                      {processor.processor_type === 'ACH Processor' && paymentTypes.ACH?.length > 0 && (
                                        <>
                                          <tr className="bg-green-100">
                                            <td colSpan={processorServices.length + 1} className="py-2 px-4 font-bold text-green-900">
                                              <div className="flex items-center">
                                                <Landmark className="w-4 h-4 mr-2" />
                                                ACH Payments
                                              </div>
                                            </td>
                                          </tr>
                                          {paymentTypes.ACH.map(pt => (
                                            <tr key={pt.code} className="border-b border-slate-200 hover:bg-slate-50">
                                              <td className="py-3 px-4 font-semibold text-slate-700 sticky left-0 bg-white">
                                                {pt.name}
                                              </td>
                                              {processorServices.map(service => {
                                                const feeType = getFeeType(processor.id, service.id, pt.code);
                                                const isFlat = feeType === 'flat';
                                                return (
                                                  <td key={service.id} className="text-center py-3 px-4">
                                                    {editingMatrix[processor.id] ? (
                                                      <div className="flex flex-col items-center space-y-1">
                                                        <div className="flex items-center space-x-1">
                                                          <input
                                                            type="text"
                                                            value={getMatrixValue(processor, service.id, pt.code, true)}
                                                            onChange={(e) => {
                                                              const value = e.target.value;
                                                              // Allow numbers and single decimal point
                                                              if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                                                handleMatrixCellChange(processor.id, service.id, pt.code, value);
                                                              }
                                                            }}
                                                            className="w-20 px-2 py-1 border border-slate-300 rounded text-center focus:ring-2 focus:ring-green-500 focus:border-green-500 text-sm"
                                                            placeholder={isFlat ? "0.00" : "0.00"}
                                                          />
                                                          <button
                                                            type="button"
                                                            onClick={(e) => {
                                                              e.preventDefault();
                                                              toggleFeeType(processor.id, service.id, pt.code);
                                                            }}
                                                            className={`px-3 py-1 text-sm font-bold border-2 rounded transition-all ${
                                                              isFlat 
                                                                ? 'bg-green-600 text-white border-green-700 hover:bg-green-700' 
                                                                : 'bg-indigo-600 text-white border-indigo-700 hover:bg-indigo-700'
                                                            }`}
                                                            title={isFlat ? "Flat fee - Click to switch to percentage" : "Percentage - Click to switch to flat fee"}
                                                          >
                                                            {isFlat ? '$' : '%'}
                                                          </button>
                                                        </div>
                                                      </div>
                                                    ) : (
                                                      <span className="font-mono text-slate-900 font-semibold">
                                                        {isFlat 
                                                          ? `$${getMatrixValue(processor, service.id, pt.code).toFixed(2)}`
                                                          : `${getMatrixValue(processor, service.id, pt.code).toFixed(2)}%`
                                                        }
                                                      </span>
                                                    )}
                                                  </td>
                                                );
                                              })}
                                            </tr>
                                          ))}
                                        </>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal 
        deleteConfirm={deleteConfirm}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm(null)}
      />

      {/* Add Modals */}
      <AddProcessorModal
        show={showAddProcessor && !editingProcessor?.id}
        onClose={() => { setShowAddProcessor(false); setEditingProcessor(null); }}
        processor={editingProcessor}
        setProcessor={setEditingProcessor}
        onSave={handleAddProcessor}
        processorTypes={PROCESSOR_TYPES}
      />

      <AddPaymentTypeModal
        show={showAddPaymentType && !editingPaymentType?.id}
        onClose={() => { setShowAddPaymentType(false); setEditingPaymentType(null); }}
        paymentType={editingPaymentType}
        setPaymentType={setEditingPaymentType}
        onSave={handleAddPaymentType}
      />
    </div>
  );
}
